import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  catchError,
  first,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {AccountStorePort} from 'src/app/core/entities/account/domain/ports/account-store.port';
import {ACCOUNT_STORE} from 'src/app/core/entities/account/domain/tokens/account-store.token';
import {TicketGeneratorService} from 'src/app/core/entities/ticket/application/services/ticket-generator.service';
import {TicketFlags} from 'src/app/pages/service/pages/account-page/enum/ticket-flags.enum';
import {Account} from '../../entities/account/domain/model/account.model';
import {AuditService} from '../../entities/audit/audit.service';
import {DeviceService} from '../../entities/device/device.service';
import {
  OrganizationRepositoryAdapter
} from '../../entities/organization/infrastructure/adapters/organization-repository.adapter';
import {Payment} from '../../entities/payment/domain/model/payment.model';
import {TicketEvent} from '../../entities/ticket-events/interface/ticket-event.model';
import {TicketEventSocketHandlerService} from '../../entities/ticket-events/ticket-event-socket-handler.service';
import {TicketEventService} from '../../entities/ticket-events/ticket-event.service';
import {Ticket} from '../../entities/ticket/domain/model/ticket.model';
import {TICKET_REPOSITORY} from '../../entities/ticket/domain/tokens/ticket.token';
import {INVOICING_STATUS} from '../../enums/invoicing-status.enum';
import {IAudit} from '../../interfaces/audit.interface';
import {Logger} from '../../interfaces/Logger';
import {
  IPaymentDetails,
  IPaymentIndexedDBData,
} from '../../interfaces/payment.interface';
import {TrackingService} from '../tracking/tracking.service';
import {createPaymentTracker} from '../tracking/utils/payment-tracker.util';
import {PaymentsSocketHandlerService} from './payments-socket-handler.service';
import {FeatureFlagAdapter} from "@shared/infrastructure/adapters/feature-flag.adapter";

import { SERVICE_STATE_SERVICE } from '@features/service/domain/tokens/service-state.token';
import { Worker } from '@features/service/domain/models/worker.model';

/**
 * Service for handling common payment operations across the application.
 * Provides methods for ticket generation, payment processing, and related operations.
 */
@Injectable({
  providedIn: 'root',
})
export class PaymentsCommonService {
  private readonly organizationService = inject(OrganizationRepositoryAdapter);
  private readonly accountStoreService = inject<AccountStorePort>(ACCOUNT_STORE);
  private readonly auditService = inject(AuditService);
  private readonly ticketGeneratorService = inject(TicketGeneratorService);
  private readonly ticketEventService = inject(TicketEventService);
  private readonly deviceService = inject(DeviceService);
  private readonly logger = inject(Logger);
  private readonly trackingService = inject(TrackingService);
  private readonly paymentSocketHandler = inject(PaymentsSocketHandlerService);
  private readonly ticketEventSocketHandlerService = inject(TicketEventSocketHandlerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureFlagService = inject(FeatureFlagAdapter);
  private readonly trackPayment = createPaymentTracker(this.trackingService);
  private readonly ticketRepository = inject(TICKET_REPOSITORY);

  private readonly serviceStateService = inject(SERVICE_STATE_SERVICE);

  /**
   * Handles ticket generation process based on payment details.
   *
   * @param paymentDetails - Details of the payment being processed
   * @param currentAccount - The current account associated with the payment
   * @param payment - The payment object
   * @returns An observable of the generated ticket or null if no ticket is generated
   */
  handleTicketGeneration(
    paymentDetails: IPaymentDetails,
    currentAccount: Account | null,
    payment: Payment
  ): Observable<Ticket | null> {
    if (!paymentDetails.isTicketGenerated) {
      this.trackPayment(`Ticket is not generated. Skiping`);

      return of(null);
    }

    return this.ticketGeneratorService
      .generateSimpleTicket(currentAccount, payment)
      .pipe(first());
  }

  /**
   * Handles the insertion of a ticket into the system.
   * Updates device series if needed and inserts the ticket into storage.
   *
   * @param ticket - The ticket to be inserted
   * @param increaseSerie - Whether to increase the device's series number
   * @returns An observable that emits the inserted ticket or null if no ticket was provided
   */
  handleTicketInsertion(
    ticket: Ticket | null,
    increaseSerie: boolean
  ): Observable<Ticket | null> {
    if (!ticket) {
      return of(null);
    }

    const device = this.deviceService.device;

    const isRolloutPosInvoiceModuleEnabled =
      this.featureFlagService.isFlagActivated(
        TicketFlags.ROLLOUT_POS_INVOICE_MODULE
      );

    const oldSerie = device?.getSimpleSerie(isRolloutPosInvoiceModuleEnabled);

    if (increaseSerie) {
      device?.increaseSimpleSerie(isRolloutPosInvoiceModuleEnabled);
    }

    const newSerie = device?.getSimpleSerie(isRolloutPosInvoiceModuleEnabled);

    if (isRolloutPosInvoiceModuleEnabled) {
      ticket.updateStatus(INVOICING_STATUS.pending);
    }

    this.trackPayment(`Increased serie ${newSerie}. Old serie ${oldSerie}`);

    const insertTicketAndDevice$ = this.ticketRepository
      .saveTicket(ticket)
      .pipe(switchMap(() => this.deviceService.insertDevice(device)));

    const updateLastTicket$ = this.ticketRepository.saveLastTicket(ticket);

    return forkJoin({
      deviceInserted: insertTicketAndDevice$,
      updateLastTicket: updateLastTicket$,
    }).pipe(
      tap(({deviceInserted}) => {
        this.trackPayment('Device inserted', {
          customData: {
            deviceSerie: deviceInserted?.simple_serie,
          },
        });
        this.trackPayment('Ticket inserted', {
          customData: {
            ticketSerie: ticket.serie,
          },
        });
      }),
      map(() => ticket)
    );
  }

  /**
   * Retrieves all necessary payment-related data from IndexedDB.
   * Collects drawer, waiter, organization, account, audit, and ticket event information.
   *
   * @returns An observable that emits an object containing all required payment data
   */
  getNecessaryPaymentDataFromIndexedDB(): Observable<IPaymentIndexedDBData> {
    return forkJoin({
      currentDrawer: of(this.serviceStateService.getCurrentDrawer()),
      currentWorker: of(this.serviceStateService.getCurrentWorker()),
      organization: this.organizationService.getCurrentOrganization(),
      currentAccount: of(this.accountStoreService.getSelectedAccount() ?? null),
      currentAudit: this.auditService.getCurrentAuditAccount(),
      currentTicketEvent: this.ticketEventService.getCurrentTicketEvent(),
    }).pipe(
      first(),
      catchError((error) => {
        this.logger.error(
          new Error('Error retrieving payment necessary data:'),
          error
        );

        return of({
          currentDrawer: null,
          currentWorker: null,
          organization: null,
          currentAccount: null,
          currentAudit: null,
          currentTicketEvent: null,
        });
      })
    );
  }

  /**
   * Sends a payment event through the socket handler.
   * Notifies the system about a manual account payment.
   *
   * @param payAccountData - Object containing all data required for processing the payment
   */
  sendPayManualAccountAmountEvent(payAccountData: {
    currentAccount: Account | null;
    currentWorker: Worker | null;
    payment: Payment;
    currentAudit: IAudit | null;
    paymentToSend: IPaymentDetails;
    ticket: Ticket | null;
  }): void {
    const {
      currentAccount,
      currentWorker,
      payment,
      currentAudit,
      paymentToSend,
      ticket,
    } = payAccountData;

    this.logger.debug(`****TICKET SERIE**** : ${payment.serie}`);

    this.paymentSocketHandler
      .sendPayAccountAmountEvent(
        currentAccount,
        currentWorker,
        payment,
        currentAudit,
        paymentToSend.isAccountClosed,
        ticket
      )
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (error) =>
          this.logger.error(new Error('Error sending socket payment'), error),
      });
  }

  /**
   * Updates a ticket event with payment information and sends the update via socket.
   *
   * @param ticketEvent - The ticket event to update
   * @param payment - The payment information to include in the update
   */
  updateTicketEvent(ticketEvent: TicketEvent, payment: Payment): void {
    const updatedTicketEvent =
      this.ticketEventService.updateTicketEventComplementsByPayment(
        ticketEvent,
        payment
      );

    this.ticketEventSocketHandlerService
      .sendUpdateTicketEventEvent(updatedTicketEvent)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.trackPayment(`Ticket event updated`);
  }
}
